import type React from 'react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useStream } from '@langchain/langgraph-sdk/react'
import type { Message, AIMessage } from '@langchain/langgraph-sdk'
import { Button, ChatBubble, ChatTypingIndicator, Chip } from '@knownow/ui'
import { useSelectionStore } from '@/stores/selection-store'
import { useAuthenticatedClient } from '@/hooks/use-authenticated-client'
import { MarkdownContent } from '@/components/markdown-content'
import { ToolUseIndicator } from '@/components/tool-use-indicator'

const AGENT_URL =
  (import.meta.env['VITE_AGENT_URL'] as string | undefined) ?? 'http://127.0.0.1:3001'

export interface ChatPanelProps {
  readonly planId: string | null
  readonly organizationId: string | null
}

function getMessageText(message: Message): string {
  const { content } = message
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .map((block) => {
        if (typeof block === 'string') return block
        if (typeof block === 'object' && block !== null && 'text' in block) {
          return (block as { text: string }).text
        }
        return ''
      })
      .join('')
  }
  return ''
}

function getToolNames(message: Message): string[] {
  if (message.type !== 'ai') return []
  const calls = (message as AIMessage).tool_calls ?? []
  return calls.map((tc) => tc.name).filter((n): n is string => typeof n === 'string' && n.length > 0)
}

export function ChatPanel({ planId, organizationId }: ChatPanelProps): React.ReactElement {
  const [input, setInput] = useState('')
  const threadRef = useRef<HTMLDivElement>(null)
  const selection = useSelectionStore((s) => s.selection)
  const clearSelection = useSelectionStore((s) => s.clearSelection)
  const { client, isReady } = useAuthenticatedClient(AGENT_URL)

  // Use the planId as a stable thread_id so the agent chat thread is
  // persisted per plan: opening a plan rehydrates its existing messages
  // from the LangGraph checkpointer instead of starting an empty thread.
  const thread = useStream({
    client,
    assistantId: 'survey-agent',
    threadId: isReady ? planId : null,
  })

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [thread.messages])

  const handleSend = useCallback((): void => {
    const trimmed = input.trim()
    if (!trimmed || thread.isLoading || !planId) return

    let content = trimmed
    if (selection) {
      const fieldSuffix = selection.field ? `, field: ${selection.field}` : ''
      content += `\n\n[Selection context: Information need "${selection.informationNeedDescription}" (ID: ${selection.informationNeedId})${fieldSuffix}]`
    }

    thread.submit(
      { messages: [{ type: 'human', content }] },
      {
        config: {
          configurable: {
            planId,
            organizationId: organizationId ?? '',
          },
        },
      },
    )

    setInput('')
    clearSelection()
  }, [input, thread, planId, organizationId, selection, clearSelection])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const visibleMessages = thread.messages.filter(
    (m: Message) => m.type === 'human' || m.type === 'ai',
  )

  return (
    <div className="flex h-full flex-col" data-testid="chat-panel">
      <div ref={threadRef} className="flex-1 overflow-y-auto p-4" data-testid="message-thread">
        {visibleMessages.length === 0 && !thread.isLoading && (
          <p className="text-neutral-500 text-sm">
            Describe what you want to learn. The AI will help you build your research plan.
          </p>
        )}
        <div className="flex flex-col gap-3">
          {visibleMessages.map((msg: Message, i: number) => {
            const text = getMessageText(msg)
            const isAi = msg.type === 'ai'
            const toolNames = isAi ? getToolNames(msg) : []
            const showToolIndicator = isAi && text.trim() === '' && toolNames.length > 0
            if (showToolIndicator) {
              return (
                <ChatBubble key={msg.id ?? i} variant="ai">
                  <ToolUseIndicator toolNames={toolNames} />
                </ChatBubble>
              )
            }
            return (
              <ChatBubble key={msg.id ?? i} variant={isAi ? 'ai' : 'user'}>
                {isAi ? <MarkdownContent content={text} /> : text}
              </ChatBubble>
            )
          })}
          {thread.isLoading && <ChatTypingIndicator testId="streaming-indicator" />}
        </div>
        {thread.error != null && (
          <div className="mt-3 rounded-md bg-error-50 p-3 text-sm text-error-700" data-testid="error-message">
            {thread.error instanceof Error ? thread.error.message : String(thread.error)}
          </div>
        )}
      </div>

      <div className="border-t border-neutral-200 p-3">
        {selection && (
          <div className="mb-2" data-testid="selection-badge">
            <Chip variant="selected" onDismiss={clearSelection}>
              Talking about: {selection.informationNeedDescription}
            </Chip>
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e): void => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your research plan..."
            rows={1}
            className="flex-1 resize-none rounded-md border border-neutral-300 bg-neutral-0 px-3 py-2 text-sm font-sans placeholder:text-neutral-500 focus:border-brand-500 focus:outline-hidden focus:ring-2 focus:ring-brand-500"
            data-testid="chat-input"
          />
          <Button
            variant="primary"
            size="md"
            onClick={handleSend}
            disabled={!input.trim() || thread.isLoading}
            data-testid="send-button"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  )
}
